import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { QRCodeSVG } from 'qrcode.react';
import { 
    QrCode, 
    Download, 
    Printer, 
    MapPin,
    Table2,
    ExternalLink,
    Grid3X3
} from 'lucide-react';

const TableQRCodesPage = () => {
    const { api } = useAuth();
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedZone, setSelectedZone] = useState('all');
    const [selectedTable, setSelectedTable] = useState(null);
    const printRef = useRef();

    // Get base URL for QR codes
    const baseUrl = window.location.origin;

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await api().get('/tables');
            setTables(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des tables');
        } finally {
            setLoading(false);
        }
    };

    // Get unique zones
    const zones = [...new Set(tables.map(t => t.zone))].sort();

    // Filter tables by zone
    const filteredTables = selectedZone === 'all' 
        ? tables 
        : tables.filter(t => t.zone?.toLowerCase() === selectedZone.toLowerCase());

    // Generate QR code URL for a table
    const getTableMenuUrl = (table) => {
        return `${baseUrl}/client/menu?table=${table.number}`;
    };

    // Download single QR code as PNG
    const downloadQRCode = (table) => {
        const svg = document.getElementById(`qr-${table.id}`);
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = 400;
            canvas.height = 500;
            
            // White background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Draw QR code
            ctx.drawImage(img, 50, 50, 300, 300);
            
            // Add text
            ctx.fillStyle = '#166534';
            ctx.font = 'bold 24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(`Table ${table.number}`, canvas.width / 2, 400);
            ctx.font = '16px Arial';
            ctx.fillStyle = '#666';
            ctx.fillText(table.zone || 'Salle', canvas.width / 2, 430);
            ctx.font = '14px Arial';
            ctx.fillText('Scannez pour voir le menu', canvas.width / 2, 470);

            const link = document.createElement('a');
            link.download = `QR-Table-${table.number}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    // Print all QR codes
    const printAllQRCodes = () => {
        const printWindow = window.open('', '_blank');
        const qrCodes = filteredTables.map(table => `
            <div style="page-break-inside: avoid; display: inline-block; width: 45%; margin: 2%; padding: 20px; border: 2px solid #166534; border-radius: 16px; text-align: center;">
                <div style="margin-bottom: 10px;">
                    <svg viewBox="0 0 256 256" width="180" height="180">
                        ${document.getElementById(`qr-${table.id}`)?.innerHTML || ''}
                    </svg>
                </div>
                <h2 style="color: #166534; margin: 10px 0 5px; font-size: 28px;">Table ${table.number}</h2>
                <p style="color: #666; margin: 5px 0; font-size: 14px;">${table.zone || 'Salle'}</p>
                <p style="color: #166534; margin: 10px 0 0; font-size: 12px; font-weight: bold;">Scannez pour découvrir notre menu</p>
            </div>
        `).join('');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>QR Codes Tables - Aldrin's</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    @media print {
                        body { padding: 0; }
                    }
                </style>
            </head>
            <body>
                <div style="text-align: center; margin-bottom: 30px;">
                    <h1 style="color: #166534;">Restaurant Aldrin's</h1>
                    <p>QR Codes des Tables</p>
                </div>
                <div style="display: flex; flex-wrap: wrap; justify-content: center;">
                    ${qrCodes}
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 500);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <QrCode className="w-8 h-8 text-green-600" />
                    QR Codes des Tables
                </h1>
                <p className="text-gray-600 mt-2">
                    Générez et imprimez les QR codes pour permettre aux clients de consulter le menu
                </p>
            </div>

            {/* Controls */}
            <Card className="mb-6">
                <CardContent className="pt-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <Select value={selectedZone} onValueChange={setSelectedZone}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Zone" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes les zones</SelectItem>
                                    {zones.map(zone => (
                                        <SelectItem key={zone} value={zone?.toLowerCase()}>
                                            {zone}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex-1" />

                        <Button 
                            onClick={printAllQRCodes}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            <Printer className="w-4 h-4 mr-2" />
                            Imprimer tout ({filteredTables.length})
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="mb-6 bg-green-50 border-green-200">
                <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <QrCode className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-green-800">Comment utiliser les QR codes ?</h3>
                            <ul className="text-sm text-green-700 mt-2 space-y-1">
                                <li>• Imprimez les QR codes et placez-les sur chaque table</li>
                                <li>• Les clients peuvent scanner avec leur téléphone</li>
                                <li>• Ils accèdent directement au menu avec photos et descriptions</li>
                                <li>• La prise de commande reste effectuée par le serveur</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* QR Codes Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" ref={printRef}>
                {filteredTables.map(table => (
                    <Card 
                        key={table.id} 
                        className={`transition-all hover:shadow-lg cursor-pointer ${
                            selectedTable?.id === table.id ? 'ring-2 ring-green-500' : ''
                        }`}
                        onClick={() => setSelectedTable(table)}
                    >
                        <CardContent className="p-4 text-center">
                            <div className="bg-white p-3 rounded-lg mb-3 inline-block">
                                <QRCodeSVG
                                    id={`qr-${table.id}`}
                                    value={getTableMenuUrl(table)}
                                    size={120}
                                    level="M"
                                    includeMargin={false}
                                    fgColor="#166534"
                                />
                            </div>
                            <h3 className="font-bold text-lg text-green-800">
                                Table {table.number}
                            </h3>
                            <Badge variant="outline" className="mt-1">
                                {table.zone || 'Salle'}
                            </Badge>
                            <div className="mt-3 flex gap-2 justify-center">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        downloadQRCode(table);
                                    }}
                                >
                                    <Download className="w-3 h-3" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(getTableMenuUrl(table), '_blank');
                                    }}
                                >
                                    <ExternalLink className="w-3 h-3" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {filteredTables.length === 0 && (
                <Card className="text-center py-12">
                    <CardContent>
                        <Table2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">Aucune table dans cette zone</p>
                    </CardContent>
                </Card>
            )}

            {/* Selected Table Preview */}
            {selectedTable && (
                <Card className="fixed bottom-4 right-4 w-80 shadow-xl border-2 border-green-500">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center justify-between">
                            <span>Table {selectedTable.number}</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedTable(null)}
                            >
                                ×
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                        <div className="bg-white p-4 rounded-lg inline-block mb-3">
                            <QRCodeSVG
                                value={getTableMenuUrl(selectedTable)}
                                size={150}
                                level="H"
                                fgColor="#166534"
                            />
                        </div>
                        <p className="text-sm text-gray-500 mb-3 break-all">
                            {getTableMenuUrl(selectedTable)}
                        </p>
                        <div className="flex gap-2">
                            <Button
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                onClick={() => downloadQRCode(selectedTable)}
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => window.open(getTableMenuUrl(selectedTable), '_blank')}
                            >
                                <ExternalLink className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default TableQRCodesPage;
